import { clsx } from 'clsx';
import { format, formatDistanceToNow } from 'date-fns';
import { useState } from 'react';

import { ONE } from '@/constants';
import { useCurrentUser, useSessions } from '@/hooks';
import { isAdmin, type Session, SessionStatus, type User } from '@/types';
import {
  Alert,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  TextField,
  Typography,
} from '@/ui';

import SessionCard from '../Card';
import Empty from '../Empty';
import SessionsLoading from '../SessionsLoading';

import type React from 'react';
import type { FC } from 'react';

import { useExtendSessionsForUserMutation } from '@/store/api/sessionApi';

type SessionsOverviewProps = {
  user: User;
};

const SessionsOverview: FC<SessionsOverviewProps> = ({ user }) => {
  const { user: currentUser } = useCurrentUser();
  const [extendOpen, setExtendOpen] = useState(false);
  const [extendCount, setExtendCount] = useState<string>('20');
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [successMessage, setSuccessMessage] = useState<string>('');
  const [extendSessions, { isLoading: isExtending }] =
    useExtendSessionsForUserMutation();

  const {
    data: nextSessionData = {
      items: {} as Record<string, Session[]>,
      total: 0,
    },
    isLoading: nextSessionLoading,
    refetch: refetchUpcoming,
  } = useSessions(
    {
      limit: ONE,
      page: ONE,
      statuses: [SessionStatus.ACTIVE, SessionStatus.RESCHEDULED],
      userId: user.id,
    },
    'always',
  );

  const {
    data: previousSessionData = {
      items: {} as Record<string, Session[]>,
    },
    isLoading: previousSessionLoading,
  } = useSessions(
    {
      limit: ONE,
      order: 'desc',
      page: ONE,
      statuses: [SessionStatus.DONE],
      userId: user.id,
    },
    'always',
  );

  const nextSessions = Object.values<Session[]>(nextSessionData.items)[0];
  const topNextSession = nextSessions ? nextSessions[0] : null;
  type PagedSessions = { total?: number };
  const upcomingTotal = (nextSessionData as PagedSessions).total ?? 0;
  const showExtend = isAdmin(currentUser) && upcomingTotal <= 4;

  const onOpenExtend = () => {
    setErrorMessage('');
    setExtendOpen(true);
  };
  const onCloseExtend = () => {
    setExtendOpen(false);
    setErrorMessage('');
  };
  const onChangeCount = (e: React.ChangeEvent<HTMLInputElement>) =>
    setExtendCount(e.target.value);
  const onSubmitExtend = async () => {
    const n = parseInt(extendCount || '0', 10);
    if (!Number.isFinite(n) || n <= 0) return;
    try {
      const res = await extendSessions({
        extendByCount: n,
        userId: user.id,
      }).unwrap();
      let bookedThrough = '';
      const booked =
        (res as { booked_until?: string }).booked_until ??
        (res as { bookedUntil?: string }).bookedUntil;
      if (booked) bookedThrough = format(new Date(booked), 'MMM d, yyyy');
      setSuccessMessage(
        `${res.created} ${res.created === 1 ? 'session' : 'sessions'} booked out through ${bookedThrough}`,
      );
      onCloseExtend();
      refetchUpcoming();
    } catch (err: unknown) {
      const raw = (err as { data?: unknown }).data;
      const message =
        (typeof raw === 'string' && raw) ||
        (raw &&
        typeof raw === 'object' &&
        typeof (raw as { error?: unknown }).error === 'string'
          ? ((raw as { error?: string }).error as string)
          : '') ||
        (raw &&
        typeof raw === 'object' &&
        Array.isArray((raw as { errors?: unknown }).errors)
          ? ((raw as { errors?: unknown[] }).errors as unknown[])
              .map((e) => String(e))
              .join('; ')
          : '') ||
        'Failed to extend sessions';
      setErrorMessage(message);
    }
  };

  const previousSessions = Object.values<Session[]>(
    previousSessionData.items,
  )[0];
  const topPreviousSession = previousSessions ? previousSessions[0] : null;

  if (nextSessionLoading || previousSessionLoading) return <SessionsLoading />;

  return (
    <Card>
      {successMessage && (
        <CardContent>
          <Alert severity="success">{successMessage}</Alert>
        </CardContent>
      )}
      <CardContent>
        {topNextSession ? (
          <>
            <SessionCard
              session={topNextSession}
              title={`Next session ${formatDistanceToNow(
                new Date(topNextSession.startedAt),
                {
                  addSuffix: true,
                },
              )}`}
            />
            {showExtend && (
              <div className="mt-4 flex justify-end">
                <Button size="small" variant="outlined" onClick={onOpenExtend}>
                  Extend sessions
                </Button>
              </div>
            )}
            <Divider
              className={clsx('-mx-6', 'my-2', {
                '-mb-5': !!topPreviousSession,
              })}
            />
          </>
        ) : (
          <Empty client={user} />
        )}
      </CardContent>

      <Dialog open={extendOpen} onClose={onCloseExtend}>
        <DialogTitle>Extend sessions</DialogTitle>
        <DialogContent>
          {errorMessage && (
            <Alert className="mb-3" severity="error">
              {errorMessage}
            </Alert>
          )}
          <Typography className="mb-3" variant="body2">
            How many additional sessions would you like to add?
          </Typography>
          <TextField
            inputMode="numeric"
            label="Count"
            margin="dense"
            type="number"
            value={extendCount}
            autoFocus
            fullWidth
            onChange={onChangeCount}
          />
        </DialogContent>
        <DialogActions>
          <Button color="gray" onClick={onCloseExtend}>
            Cancel
          </Button>
          <Button
            disabled={isExtending}
            variant="contained"
            onClick={onSubmitExtend}
          >
            Extend
          </Button>
        </DialogActions>
      </Dialog>

      {!!topPreviousSession && (
        <CardContent>
          <SessionCard
            session={topPreviousSession}
            title="Previous session"
            hideCalendarOptions
          />
        </CardContent>
      )}
    </Card>
  );
};

export default SessionsOverview;
